import { FormEvent, useState } from "react";
import { ChevronLeft } from "lucide-react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { HttpStatusCode } from "axios";
import Input from "../input/Input";
import { useAppDispatch } from "../../app/hooks";
import { resetPasswordAsync } from "../../features/authentication/authenticationApi";
import { ROUTES } from "../../routes";
import { validateEmail } from "./authValidation";

interface ResetPasswordFormProps {
  onCancel: () => void;
}

const ResetPasswordForm = ({ onCancel }: ResetPasswordFormProps) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const navigate = useNavigate();

  const [email, setEmail] = useState<string>("");
  const [emailError, setEmailError] = useState<string | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);

  const handleSubmit = async (event: FormEvent) => {
    event.preventDefault();
    setError(null);
    setEmailError(null);

    if (email.trim() === "") {
      setEmailError(t("auth.errors.emailRequired"));
      return;
    }

    if (!validateEmail(email)) {
      setEmailError(t("auth.errors.invalidEmailFormat"));
      return;
    }

    setLoading(true);

    try {
      const resultAction = await dispatch(resetPasswordAsync(email));

      if (resetPasswordAsync.fulfilled.match(resultAction)) {
        navigate(ROUTES.RESET_PASSWORD_EMAIL_SENT, { state: { email } });
      } else if (resetPasswordAsync.rejected.match(resultAction)) {
        const errorResponse = resultAction.payload;
        switch (errorResponse?.status) {
          case HttpStatusCode.BadRequest:
            setError(t("auth.errors.invalidEmailFormat"));
            break;
          case HttpStatusCode.NotFound:
            setError(t("auth.errors.emailNotRegistered"));
            break;
          case HttpStatusCode.InternalServerError:
            setError(t("auth.errors.serverError"));
            break;
          default:
            setError(errorResponse?.message || t("auth.errors.unknownError"));
            break;
        }
      }
    } catch {
      setError(t("auth.errors.unexpectedError"));
    } finally {
      setLoading(false);
    }
  };

  return (
    <form onSubmit={handleSubmit} noValidate>
      <button
        type="button"
        className="mb-4 flex items-center gap-1 text-sm cursor-pointer hover:underline text-primary-40 dark:text-primary-90 bg-transparent border-none p-0"
        onClick={onCancel}
      >
        <ChevronLeft size={16} />
        {t("auth.backToSignIn")}
      </button>
      <h2 className="text-grey-20 dark:text-white">{t("auth.resetPassword")}</h2>

      <div className="mt-6">
        <Input
          label={t("auth.email")}
          type="email"
          name="resetEmail"
          id="resetEmail"
          fullWidth
          margin="dense"
          value={email}
          onChange={(e) => {
            setEmail(e.target.value);
            if (emailError) setEmailError(null);
          }}
          disabled={loading}
          error={!!emailError}
          helperText={emailError}
          labelPosition="top-left"
        />
      </div>

      {error && (
        <p className="mb-3 text-sm p-2 rounded-lg text-red-600 dark:text-red-400 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800">
          {error}
        </p>
      )}

      <button
        type="submit"
        className="btn btn-primary w-full h-[46px] mt-2 flex items-center justify-center text-[16px] no-underline"
        disabled={loading || !validateEmail(email)}
      >
        {t("auth.resetPassword")}
      </button>
    </form>
  );
};

export default ResetPasswordForm;
